import { isAxiosError } from 'axios';
import useToast from 'hooks/useToast';
import { Logger } from 'utils/logger';
import { emitErrorLog } from 'utils/observability/logger';
import { serializeError } from 'utils/observability/serializeError';
import { isLocalServer } from 'constant/server';
import { MESSAGE } from 'constant/message';
import { humanizeFieldName } from 'utils/string/transform';

interface ErrorHandlerProps {
  error?: Error;
  showToast?: boolean;
  fallbackMessage?: string;
  errorKey?: string;
}

/**
 * Parses API validation payloads often shaped as:
 * `{ error: { phone_number: ['Invalid phone number'], email: ['...'] } }`
 * Field keys are dynamic; values are string or string[].
 */

const messageFromFieldErrorObject = (
  errorPayload: unknown,
): string | undefined => {
  if (errorPayload == null) return undefined;
  if (typeof errorPayload === 'string') {
    return errorPayload;
  }
  if (Array.isArray(errorPayload)) {
    const strings = errorPayload.filter(
      (item): item is string => typeof item === 'string',
    );
    return strings.length > 0 ? strings.join(', ') : undefined;
  }
  if (typeof errorPayload !== 'object') return undefined;
  const entries = Object.entries(errorPayload as Record<string, unknown>);
  const segments: string[] = [];
  for (const [field, raw] of entries) {
    let messages: string[] = [];
    if (Array.isArray(raw)) {
      messages = raw.filter((item): item is string => typeof item === 'string');
    } else if (typeof raw === 'string') {
      messages = [raw];
    }
    if (messages.length === 0) continue;
    const text = messages.join(', ');
    segments.push(
      entries.length === 1 ? text : `${humanizeFieldName(field)}: ${text}`,
    );
  }
  if (segments.length === 0) return undefined;
  return segments.join('. ');
};

/**
 * @description Global Error Handler
 */
export const useErrorHandler = () => {
  const { toast } = useToast();

  const handleError = ({
    error,
    showToast = true,
    fallbackMessage,
    errorKey = 'message',
  }: ErrorHandlerProps) => {
    if (isLocalServer && error) {
      Logger.error({
        message: 'Error handled by useErrorHandler',
        data: error,
      });
    }

    // serializeError is required because { ...error } produces {} — Error and
    // AxiosError properties (message, stack, status, etc.) are non-enumerable.
    if (!isLocalServer) {
      emitErrorLog({
        body: {
          message: 'Error handled by useErrorHandler',
          error: serializeError(error),
        },
        attrs: {
          source: 'UseErrorHandler',
        },
      });
    }

    let errorMessage = '';

    if (typeof error === 'string') {
      errorMessage = error;
    } else if (isAxiosError(error) && error?.response) {
      const responseData = error?.response?.data as Record<string, unknown> & {
        error?: string;
        message?: string;
        detail?: string;
      };

      const rawMessageByErrorKey = responseData?.[errorKey];
      const messageByErrorKey =
        typeof rawMessageByErrorKey === 'string' ? rawMessageByErrorKey : null;

      const fromFieldErrors = messageFromFieldErrorObject(
        responseData?.error as unknown,
      );

      errorMessage =
        messageByErrorKey ||
        fromFieldErrors ||
        responseData?.message ||
        responseData?.detail ||
        error?.message ||
        '';
    } else if (error instanceof Error) {
      errorMessage = error?.message;
    }

    if (showToast) {
      const errorMsg =
        typeof errorMessage === 'string' && errorMessage !== ''
          ? errorMessage
          : fallbackMessage || MESSAGE.DEFAULT_ERROR_MESSAGE;

      toast.error(errorMsg);
    }
  };

  return { handleError };
};
